import {graphql, PageProps} from 'gatsby';
import React, {Fragment, memo, useEffect, useState} from 'react';

import DATA_EVENTS from '../../tmp/data-extern/events.json';
import DATA_EVENTMINIS from '../../tmp/data-extern/eventminis.json';
import EventCategory from '../components/events/event-category';
import PageElementGroup from '../components/page-element-group';
import EventMini from '../components/events/event-mini';
import Block from '../components/block';
import {
	buildEventSourceUrl,
	processEventData,
	processSerializedEvents,
	processEventMiniData,
	processSerializedEventMinis,
} from '../utils/events';
import {fetch} from '../utils/fetch';
import {useLocationHash} from '../hooks';
import Prng from '../utils/prng';

const GATSBY_EVENTS_API_BASE_URL = process.env.GATSBY_EVENTS_API_BASE_URL ??
	'https://www.braunschweig.de/apps/events/export_json.php';

const GATSBY_EVENTMINIS_API_BASE_URL = process.env.GATSBY_EVENTMINIS_API_BASE_URL ??
	'https://www.braunschweig.de/apps/events/export_json_lp.php';

export const query = graphql`
	query EventsPage {
		allPagesJson(filter: {slug: {eq: "events"}}) {
			nodes {
				...PageElementGroup
			}
		}
	}
`;

export default memo(
	/**
	 * @param {PageProps<
	 *   import("../../tmp/graphql-types").EventsPageQuery,
	 *   {
	 *     hashState: number,
	 *     meta: import("../page-metadata").PageMetadata,
	 *   }
	 * >} props props
	 * @returns {React.ReactElement} ...
	 */
	function Events({data, pageContext: {hashState, meta}}) {
		const prng = new Prng(hashState);

		const [groupedEvents, setGroupedEvents] =
			useState(() => processSerializedEvents(DATA_EVENTS));

		const [eventMinis, setEventMinis] =
			useState(() => processSerializedEventMinis(DATA_EVENTMINIS));

		const [dataVersion, setDataVersion] = useState(0);

		useEffect(
			() => {
				(async () => {
					const url = buildEventSourceUrl(GATSBY_EVENTS_API_BASE_URL);
					const res = await fetch(url);
					if (res.status !== 200) {
						throw new Error(
							`Error fetching event data - status: ${res.status} url: "${url}"`
						);
					}

					const jsonStr = await res.text();
					setGroupedEvents(processEventData(JSON.parse(jsonStr).events));
					setDataVersion((prev) => prev + 1);
				})()
					.catch(console.error);

				(async () => {
					const url = buildEventSourceUrl(GATSBY_EVENTMINIS_API_BASE_URL);
					const res = await fetch(url);
					if (res.status !== 200) {
						throw new Error(
							`Error fetching event mini data - status: ${res.status} url: "${url}"`
						);
					}

					const jsonStr = await res.text();
					setEventMinis(processEventMiniData(JSON.parse(jsonStr).events));
				})()
					.catch(console.error);
			},
			[],
		);

		const locationHash = useLocationHash();

		return (
			<Fragment>
				<PageElementGroup
					name="main"
					meta={meta}
					nodes={data.allPagesJson.nodes}
					hashState={prng.next()}
				/>

				<Block padding>
					<div lang="de">
						{eventMinis.map((event) => (
							<EventMini
								key={event.id}
								event={event}
							/>
						))}
					</div>
				</Block>

				<PageElementGroup
					name="middle"
					meta={meta}
					nodes={data.allPagesJson.nodes}
					hashState={prng.next()}
				/>

				<div lang="de">
					{groupedEvents.map((events) => (
						<EventCategory
							key={events[0].category}
							events={events}
							dataVersion={dataVersion}
							hashOpenedEventId={locationHash}
						/>
					))}
				</div>
			</Fragment>
		);
	}
);
